import React, { useMemo } from 'react'
import { connect } from 'react-redux'

import {
  BucketSource,
  LotGallery,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import DataHandler from '@components/data-handler'
import Skeleton from '@components/skeleton/v2'

import PackageInfoCard from '@pages/properties-view/house-and-land/v2/lot-view/package-info-card'

import { LotAssetInterface } from '@api/houseAndLand'
import {
  selectFromResult,
  useGetUnitGalleryQuery as useGetLotGalleryQuery,
} from '@api/unit-gallery'

import { CarouselProvider } from '@adUtilities/components/carousel-provider'
import { FileType } from '@adUtilities/constants/common'
import mapPropertyGallery, {
  FloorImage,
  PropertyGallery,
  ViewLineImage,
} from '@adUtilities/property-gallery'
import { LotProperty } from '@adUtilities/types'

export interface ComparableLotItemProps {
  comparableLot: LotProperty
  projectIdentity: ProjectIdentity
  showInfo: boolean
  fullScreenMode?: boolean
  onlyShowPropertyGalleries?: boolean
}

const NO_IMAGE_OBJECT: PropertyGallery = {
  id: 'no-image',
  src: '',
  label: 'No Image',
  type: FileType.Image,
  noSpliceUrl: false,
}

function ComparableLotItem({
  comparableLot,
  projectIdentity,
  showInfo,
  fullScreenMode,
  onlyShowPropertyGalleries,
}: ComparableLotItemProps) {
  const lot = comparableLot.data
  const { projectUUID, gallerySettings, projectName } = projectIdentity
  const { type: effectType, intervalInSeconds: autoPlayIntervalInSeconds } =
    gallerySettings || {}

  const lotGalleryQuery = useGetLotGalleryQuery(
    { projectName, unitId: lot.name },
    { selectFromResult }
  )
  const { unitGalleryData: lotGalleryData } = lotGalleryQuery

  const transformImageAsset = (item: LotAssetInterface) => ({
    id: item.id,
    src: item.url,
    label: item.title || item.name,
    order: item.order,
  })

  const slides = useMemo(() => {
    const floorItems: FloorImage[] = lot.assets?.floorImage
      ? [transformImageAsset(lot.assets.floorImage)]
      : []

    const viewLineItems: ViewLineImage[] =
      lot.assets?.viewLinesImage?.map(transformImageAsset) || []

    const propertyGalleries: PropertyGallery[] = (lotGalleryData || []).map(
      (item: LotGallery) => ({
        id: item.id,
        src: item.src,
        label: item.label || '',
        type: item.type,
        thumbnail: item.thumbnail,
        noSpliceUrl: false,
      })
    )

    const propertyGallery = mapPropertyGallery({
      projectId: projectUUID,
      floorImages: floorItems,
      viewLineImages: viewLineItems,
      propertyGalleries,
      onlyShowPropertyGallery: onlyShowPropertyGalleries || false,
    })

    const gallery = propertyGallery.length ? propertyGallery : [NO_IMAGE_OBJECT]

    return [gallery[0]].map((item) => ({
      id: item.id,
      label: item.label,
      src:
        item.type === FileType.Image && item.src
          ? item.src
          : item.thumbnail || '',
      thumbnail: item.thumbnail,
      originalSrc: item.src,
      bucketType: BucketSource.Legacy,
      fileType: item.type as FileType,
      noSpliceUrl: item.noSpliceUrl,
    }))
  }, [projectUUID, lot, lotGalleryData, onlyShowPropertyGalleries])

  return (
    <DataHandler
      payloads={{
        ...lotGalleryQuery,
        data: slides,
      }}
      skeletonFrame={<Skeleton containerClass="!w-full absolute" />}
    >
      <div className="relative flex h-full w-full bg-gray-700/50">
        <div
          className={`absolute bottom-0 z-10 h-auto w-full bg-shadow-horizontal transition duration-300 ease-in ${
            showInfo ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div
            className={`flex h-full transform items-stretch justify-end duration-200 ease-in ${
              fullScreenMode ? 'pb-2 pl-2' : 'pb-5 pr-5'
            } `}
          >
            <PackageInfoCard
              lot={lot}
              totalPrice={lot.price}
              handlePackageInfoModal={() => {}}
              packageInfo={undefined}
              activePrecinctId={comparableLot.references.precinctId}
              activeStageId={comparableLot.references.stageId}
            />
          </div>
        </div>
        <div className="absolute inset-0">
          <CarouselProvider
            gallerySettings={{ effectType, autoPlayIntervalInSeconds }}
            slides={slides}
            activeSlideIndex={0}
            showControl={false}
            mousewheel={false}
            autoPlay={false}
            toggleControls={false}
          />
        </div>
      </div>
    </DataHandler>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    projectIdentity,
    showInfo: !!session?.houseAndLand?.lotInfoPopup,
    onlyShowPropertyGalleries: projectIdentity.onlyShowPropertyGalleries,
  })
)(ComparableLotItem)
